import { NetworkStatus } from '@apollo/client';
import { startCase } from 'lodash';
import React from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import {
  TrainingPlanBasicsFragment,
  useTrainingPlanTemplatesQuery,
} from '../../../graphql/types';
import { SplitFlags } from '../../../hooks/featureFlags';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import {
  EventCategories,
  EventLabels,
  EventNames,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import GenericError from '../../reusable/GenericError';
import { FeatureHeader } from '../../reusable/Header/FeatureHeader';
import { Link } from '../../reusable/Link';
import LoadMore from '../../reusable/LoadMore';
import PaginationWaypoint from '../../reusable/PaginationWaypoint';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../../reusable/Table';
import Text, { TextColor, TextKind } from '../../reusable/Text';
import { StyledMain, StyledPage } from './styled';

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 128px;
`;

interface TemplateItemProps {
  template: TrainingPlanBasicsFragment;
}

const TemplateItem: React.FC<TemplateItemProps> = ({ template }) => {
  const { memberId } = useParams();

  return (
    <TableRow>
      <TableCell $paddingOverride="10px">
        <Link
          to={`/app/members/${memberId}/trainingPlan/${template.id}/`}
          replace
          trackingData={{
            eventName: EventNames.LinkClick,
            event_label: EventLabels.TrainingPlanTemplateSelect,
            training_plan_template_id: template.id,
            event_category: EventCategories.TrainingPlans,
            member_id: memberId,
          }}
        >
          <Text kind={TextKind.ButtonM}>{template.name}</Text>
        </Link>
      </TableCell>

      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>{startCase(template.difficulty)}</Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>{template.durationMinutes} mins</Text>
      </TableCell>
      <TableCell $paddingOverride="10px">
        <Text kind={TextKind.BodyM}>x{template.frequency}/week</Text>
      </TableCell>
    </TableRow>
  );
};

const PAGE_SIZE = 100;

interface TrainingPlanTemplatesProps {}

export const TrainingPlanTemplates: React.FC<
  TrainingPlanTemplatesProps
> = () => {
  const { flags } = useFeatureFlags();
  const enableTrainingPlansFlag = flags[SplitFlags.CH_TRAINING_PLANS];

  const {
    data: trainingPlanTemplatesData,
    fetchMore: fetchMoreTrainingPlanTemplates,
    networkStatus: trainingPlanTemplatesNetworkStatus,
    error,
  } = useTrainingPlanTemplatesQuery({
    variables: {
      input: {
        first: PAGE_SIZE,
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const isFetchingMoreTemplates =
    trainingPlanTemplatesNetworkStatus === NetworkStatus.fetchMore;
  const isLoadingTemplates =
    trainingPlanTemplatesNetworkStatus === NetworkStatus.loading;
  const templatesPageInfo =
    trainingPlanTemplatesData?.trainingPlanTemplates.pageInfo;
  const hasNextPage = templatesPageInfo?.hasNextPage;
  const endCursor = templatesPageInfo?.endCursor;

  const fetchMoreTemplates = async () => {
    if (!hasNextPage || !endCursor || isFetchingMoreTemplates) {
      return;
    }
    return await fetchMoreTrainingPlanTemplates({
      variables: {
        input: {
          first: PAGE_SIZE,
          after: endCursor,
        },
      },
    });
  };

  if (!enableTrainingPlansFlag) {
    return null;
  }

  const templates =
    trainingPlanTemplatesData?.trainingPlanTemplates.edges || [];

  return (
    <StyledPage>
      <FeatureHeader
        state="create"
        name="training plan"
        trackingData={{
          event_category: EventCategories.TrainingPlans,
        }}
      />
      <StyledMain>
        <Text kind={TextKind.TitleL}>Select a training plan template</Text>

        <TableContainer
          style={{
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          {isLoadingTemplates && <LoadingPanel noBorder height={300} />}
          {error && (
            <GenericError testId="trainingPlanTemplates-error" error={error} />
          )}
          {!error && !isLoadingTemplates && templates?.length === 0 && (
            <StyledEmptyTable>
              <Text kind={TextKind.TitleXs}>No templates found.</Text>
            </StyledEmptyTable>
          )}
          {!isLoadingTemplates && templates?.length > 0 && (
            <Table>
              <colgroup>
                <col width="auto" />
                <col width="10%" />
                <col width="10%" />
                <col width="10%" />
              </colgroup>
              <TableHead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <TableRow>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Training Plan
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Level
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Duration
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text kind={TextKind.LabelM} color={TextColor.Secondary}>
                      Frequency
                    </Text>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainingPlanTemplatesData?.trainingPlanTemplates.edges.map(
                  ({ node: template }) => (
                    <TemplateItem key={template.id} template={template} />
                  ),
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {hasNextPage && <PaginationWaypoint callback={fetchMoreTemplates} />}

        {hasNextPage && (
          <LoadMore
            loadMore={fetchMoreTemplates}
            isFetchingMore={isFetchingMoreTemplates}
          />
        )}
      </StyledMain>
    </StyledPage>
  );
};
